<template>
	<Head title="Iniciar prova" />

	<div class="background">
		<div class="container">
			<div
				class="row justify-content-lg-center justify-content-md-center">
				<div class="col-lg-12 d-flex justify-content-center p-9">
					<img
						src="../../../../public/admin/images/logo.svg"
						alt="logo da empresa" />
				</div>
			</div>

			<div class="row justify-content-center mb-10">
				<div class="col-12">
					<div class="card card-shadow p-3">
						<h3 class="text-center fw-bold">
							Para iniciar a prova pela agenda siga os passos
							abaixo:
						</h3>

						<div class="d-flex justify-content-center p-0 mt-5">
							<ol>
								<li>Acesse o calendário de agendamento.</li>
								<li>Procure o agendamento do aluno(a).</li>
								<li>Selecione o agendamento do aluno(a).</li>
								<li>Clique em "Abrir prova no computador".</li>
								<li>
									Aguarde enquanto os dados são carregados.
								</li>
								<li>A tela irá atualizar automáticamente.</li>
								<li>Confira os dados do aluno nesta tela.</li>
								<li>Clique no botão "Iniciar Prova" abaixo.</li>
							</ol>
						</div>
					</div>
				</div>
			</div>

			<div class="row justify-content-center mb-10">
				<div class="col-12">
					<div class="card card-shadow">
						<div v-if="has_error" class="p-5 text-center">
							<h2
								class="fw-bold"
								:class="{ 'text-danger': error.is_critical }">
								{{ error.message }}
							</h2>
						</div>
						<div v-else>
							<section class="d-flex justify-content-center p-5">
								<h1 v-if="empty_data" class="fw-bold">
									Nenhuma prova foi selecionada para começar
									neste computador.
								</h1>
								<ul v-else class="m-0 p-0">
									<li>
										<span class="fw-bolder">Aluno(a):</span>
										{{ enrollment_test_data.student_name }}
									</li>
									<li>
										<span class="fw-bolder">CPF:</span>
										{{ enrollment_test_data.cpf }}
									</li>
									<li>
										<span class="fw-bolder">Curso:</span>
										{{ enrollment_test_data.course }}
									</li>
								</ul>
							</section>
						</div>
					</div>
				</div>
			</div>

			<div
				class="row justify-content-center"
				v-if="!has_error && !loading && !empty_data">
				<div class="col-3">
					<loading_button
						:action="goToExam"
						classes="fs-1"
						style="height: 65"
						:loading="false"
						>Iniciar prova
					</loading_button>
				</div>
			</div>
		</div>
	</div>
	<div class="text-center">
		<p>
			© {{ new Date().getFullYear() }} <strong>Habilite-se</strong>.
			Todos os direitos reservados.
		</p>
	</div>
</template>

<script lang="ts">
	import Layout from '../../Layouts/empty_layout.vue';

	export default {
		layout: Layout,
	};
</script>

<script setup lang="ts">
	import loading_button from '../../components/Widgets/loading_button.vue';
	import { Head } from '@inertiajs/vue3';
	import axios from 'axios';
	import { Ref, computed, onMounted, ref } from 'vue';
	import { AxiosError } from 'axios';

	const props = defineProps({
		get_next_enrollment_test_url: { type: String, required: true },
	});

	interface EnrollmentTestData {
		student_name: null | string;
		cpf: null | string;
		course: null | string;
		signed_url: null | string;
	}

	interface AgentVariables {
		computer_uid: null | string;
		student_id: null | number;
	}

	interface Error {
		message: null | string;
		is_critical: null | boolean;
	}

	class AgentVariablesError extends Error {}

	const enrollment_test_data: Ref<EnrollmentTestData> = ref({
		student_name: null,
		cpf: null,
		course: null,
		signed_url: null,
	});

	const loading = ref(false);

	const error = ref({
		message: null,
		is_critical: null,
	}) as Ref<Error>;

	const agent_variables: Ref<AgentVariables> = ref({
		computer_uid: null,
		student_id: null,
	});

	const empty_data = computed(() => {
		return Object.values(enrollment_test_data.value).every(
			(value) => value === null
		);
	});

	const has_error = computed(() => {
		return Object.values(error.value).every((value) => value !== null);
	});

	async function getEnrollmentTestData(
		get_next_enrollment_test_url,
		computer_uid
	): Promise<EnrollmentTestData> {
		const url = `${get_next_enrollment_test_url}/${computer_uid}`;

		return await axios.get(url).then((response) => response.data);
	}

	function goToExam() {
		window.location.href = enrollment_test_data.value.signed_url as string;
	}

	async function getAgentVariables(): Promise<AgentVariables> {
		//@ts-ignore
		if (!window.getAgentVariables) {
			throw new AgentVariablesError(
				'Não foi possível encontrar os dados necessários para continuar, entre em contato com o suporte.'
			);
		}
		//@ts-ignore
		return await window
			.getAgentVariables()
			.then((response) => JSON.parse(response));
	}

	async function handleNextStudentExam() {
		try {
			error.value.message = null;
			error.value.is_critical = null;

			let variables = await getAgentVariables();

			if (
				variables !== null &&
				variables.student_id !== agent_variables.value.student_id
			) {
				loading.value = true;

				enrollment_test_data.value = await getEnrollmentTestData(
					props.get_next_enrollment_test_url,
					variables.computer_uid
				);

				agent_variables.value = variables;
			}
		} catch (err) {
			if (err instanceof AgentVariablesError) {
				error.value.is_critical = true;
				error.value.message = err.message;
			} else if (err instanceof AxiosError) {
				error.value.is_critical = false;
				error.value.message = err.response?.data.message;
			} else {
				error.value.is_critical = false;
				error.value.message = err as string;
			}
		} finally {
			loading.value = false;
		}
	}

	onMounted(async () => {
		setInterval(async () => {
			await handleNextStudentExam();
		}, 5000);
	});
</script>

<style scoped>
	.background {
		background: #f7f7f7 !important;
		width: 100%;
		height: 100%;
	}

	.background:before {
		content: '';
		background: url(../../../../public/admin/images/sign.svg) no-repeat
			center;
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		position: absolute;
		pointer-events: none;
		background-size: cover;
		opacity: 0.04;
	}
</style>
